import { Button, LinkButton } from "@/ui/Button";
import { ErrorNote } from "@/ui/Field";
import { GoogleLogo } from "@/ui/GoogleLogo";
import { Screen } from "@/ui/Screen";
import { Display, Label, Tiny } from "@/ui/Text";

interface SignInChoiceProps {
  readonly onGoogle: () => void;
  readonly onEmail: () => void;
  readonly error: string | null;
  readonly busy?: boolean;
}

/**
 * What signing in gets them. Every line must be true of the product today;
 * when the free allowance changes (see docs/superpowers/specs/2026-09-25-three-free-minutes.md)
 * the first line changes with it.
 */
const BENEFITS: readonly string[] = [
  "Your first three minutes are free",
  "Your chart and your week, saved",
  "She remembers what you tell her, next time too",
];

/**
 * The account, asked for at the moment someone says they want to talk. The
 * content and the buttons sit together in the middle of the screen: a page
 * that asks for something should put the reason right next to the ask.
 *
 * No Apple button on the web: Sign in with Apple needs a Services ID this
 * deployment does not have yet, and a button that fails is worse than none.
 */
export function SignInChoice({ onGoogle, onEmail, error, busy = false }: SignInChoiceProps) {
  return (
    <Screen middle>
      <Label>One step before you talk</Label>
      <Display className="mt-2">Sign in to speak to Antar.</Display>

      <ul className="mt-6 space-y-3 border-y border-rule py-5">
        {BENEFITS.map((benefit) => (
          <li key={benefit} className="flex items-start gap-3 text-[16px] leading-[1.45]">
            <svg aria-hidden width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-[3px] shrink-0">
              <path d="M4.5 12.5l5 5 10-11" />
            </svg>
            <span>{benefit}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 flex flex-col gap-2">
        <Button label="Continue with Google" icon={<GoogleLogo />} onClick={onGoogle} busy={busy} />
        <div className="text-center">
          <LinkButton label="Use email instead" onClick={onEmail} />
        </div>
      </div>
      <ErrorNote message={error} />
      <Tiny className="mt-3 text-center">No password needed. It takes a few seconds.</Tiny>
    </Screen>
  );
}
